import { PenLine, Bot, ActivitySquare } from 'lucide-react';

export function Walkthrough() {
  return (
    <section id="how-it-works" className="py-20 bg-cream scroll-mt-20 grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Three simple tools
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Journal. Coach. Track. That's it.
          </p>
        </div>

        <div className="space-y-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:pr-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-coral-100 flex items-center justify-center">
                  <PenLine className="h-6 w-6 text-coral-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900">Journal</h3>
              </div>
              <p className="text-lg text-slate-700 leading-relaxed mb-3">
                Write one line. Tag what's on your mind. Done.
              </p>
              <p className="text-slate-500">
                Build the habit of checking in with yourself. No pressure to write a novel.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-medium text-slate-900">Today</span>
                  <span className="text-xs text-coral-600 font-medium">7 day streak</span>
                </div>
                <div className="space-y-3">
                  <div className="bg-cream rounded-xl p-4 border-l-4 border-coral-400">
                    <p className="text-slate-700 text-sm mb-2">Finally hit the gym this morning. Felt good to move.</p>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 bg-sage-100 text-sage-700 text-xs rounded-full">Energy</span>
                      <span className="px-2 py-1 bg-coral-100 text-coral-700 text-xs rounded-full">Win</span>
                    </div>
                  </div>
                  <div className="bg-cream rounded-xl p-4 border-l-4 border-slate-400">
                    <p className="text-slate-700 text-sm mb-2">Anxious about the presentation tomorrow. Writing it out helps.</p>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">Stress</span>
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">Work</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2 lg:pl-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center">
                  <Bot className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900">Coach</h3>
              </div>
              <p className="text-lg text-slate-700 leading-relaxed mb-3">
                Get clear on next steps. Get nudged to follow through.
              </p>
              <p className="text-slate-500">
                Not generic motivation. Real, personalized guidance - plus accountability when you need it.
              </p>
            </div>
            <div className="lg:order-1 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-slate-900">Your Coach</span>
                  <span className="text-xs bg-sage-100 text-sage-700 px-2 py-1 rounded-full ml-auto">Ready</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-end">
                  <div className="bg-slate-100 text-slate-900 rounded-2xl rounded-br-md px-4 py-3 max-w-[80%]">
                    <p className="text-sm">I keep putting off going to the gym. How do I actually stick to it?</p>
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-coral-50 rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] border border-coral-200">
                    <p className="text-sm text-slate-700 mb-3">Start smaller than you think. Go for 10 minutes, that's it. Once you're there, you'll usually stay longer.</p>
                    <p className="text-sm text-slate-700 mb-3">Put your gym clothes out the night before. Remove the friction.</p>
                    <p className="text-sm text-slate-900 font-medium">Try this week: Go twice for just 15 minutes each. Track how you feel after.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:pr-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-sage-100 flex items-center justify-center">
                  <ActivitySquare className="h-6 w-6 text-sage-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900">Track</h3>
              </div>
              <p className="text-lg text-slate-700 leading-relaxed mb-3">
                See patterns over time. Watch yourself actually improve.
              </p>
              <p className="text-slate-500">
                Simple 1-10 scores. Proof that consistency works - even when it doesn't feel like it.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                <span className="font-medium text-slate-900">This Week</span>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Energy</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-sage-600">7</span>
                      <span className="text-xs text-sage-600">+2</span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-sage-500 rounded-full transition-all" style={{width: '70%'}} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Focus</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-coral-500">6</span>
                      <span className="text-xs text-coral-500">+1</span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-coral-500 rounded-full transition-all" style={{width: '60%'}} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Mood</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-slate-700">8</span>
                      <span className="text-xs text-sage-600">+3</span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-600 rounded-full transition-all" style={{width: '80%'}} />
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-sm text-slate-600">Average</span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-slate-900">7.0</span>
                    <span className="text-sm text-sage-600 font-medium">Best week yet!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
